import {
  REACT_APP_API_URL,
  REACT_APP_PUSH_NOTIFICATION_PUBLIC_KEY,
} from "@ch/api";
import { useAppSelector } from "@ch/redux-store/hooks/useAppSelector";
import { useEffect } from "react";

const requestBrowserPermission = async () => {
  if (!("Notification" in window)) {
    return;
  }
  await Notification.requestPermission();
};

/** VALID key must be base64url (A-Za-z0-9_-). URLs or empty strings are invalid. */
function isValidPublicKey(key: string): boolean {
  if (!key || typeof key !== "string" || key.trim().length === 0) return false;
  return /^[A-Za-z0-9_-]+$/.test(key);
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from(Array.from(rawData), (char) => char.charCodeAt(0));
}

function useWebPushNotifications() {
  const accessToken = useAppSelector((state) => state.user.accessToken);
  const currentUserId = useAppSelector((state) => state.user.currentUser?.id);

  useEffect(() => {
    void requestBrowserPermission();
    async function subscribe() {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

      const registration =
        await navigator.serviceWorker.register("/service-worker.js");

      if (!registration.active) {
        const sw = registration.installing ?? registration.waiting;
        if (sw) {
          await new Promise<void>((resolve) => {
            sw.addEventListener("statechange", () => {
              if (registration.active) resolve();
            });
            if (registration.active) resolve();
          });
        }
      }

      try {
        const key = REACT_APP_PUSH_NOTIFICATION_PUBLIC_KEY ?? "";

        if (!isValidPublicKey(key)) {
          return;
        }

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
        });

        const res = await fetch(
          `${REACT_APP_API_URL}/push-subscription/user/subscribe`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ userId: currentUserId, subscription }),
          },
        );

        const response = (await res.json()) as {
          status?: boolean | number;
          message?: string;
        };

        if (!response.status) {
          throw new Error(
            response.message ?? "Failed to subscribe for push notification",
          );
        }
      } catch {
        // Subscription is best-effort; avoid breaking the app
      }
    }

    void subscribe();
  }, [accessToken, currentUserId]);

  return null;
}

export default useWebPushNotifications;
